import { supabase } from './supabase';
import type { TransactieRecord } from './invoices';
import type { DiscountReason } from './discount-constants';

export interface KlachtOpOrder {
  id: string;
  nummer: string;
  status: string;
  type: string | null;
  bron: string | null;
  datumGeopend: string | null;
}

export interface OrderItem {
  id: string;
  itemNumber: string;
  name: string;
  description: string | null;
  productCode: string;
  lengthCm: number | null;
  widthCm: number | null;
  diameterCm: number | null;
  areaM2: number | null;
  isRound: boolean;
  hasUnderlayment: boolean;
  quantity: number;
  priceInclBtw: number;
  status: string;
  parentId: string | null;
  photosUrl: string | null;
  klantverzoekReparatie?: string | null;
  klantverzoekStatus?: 'Open' | 'Bevestigd' | 'Niet_Nodig' | null;
  klantverzoekBron?: string | null;
  klantverzoekNotitie?: string | null;
  anderePrijs: boolean;
  verkoopprijs: number | null;
  korting: number | null;
  /** Los, klant-eigen item dat NIET op de factuur komt (ondertapijt, spanbanden, …). */
  nietFactureren: boolean;
  /**
   * Factuurregel van een reparatie-opdracht. Prijs komt uit de opdracht en is
   * niet herberekenbaar, dus het prijsblok staat op slot in de bewerk-dialog.
   */
  isReparatieRegel?: boolean;
  /** Voorinspectie-bevindingen (multipicklist gesplitst op `;`). Leeg array = geen bevindingen. */
  voorinspectie: string[];
  /** Vrije-tekst-opmerkingen van wasmedewerker bij de voorinspectie. */
  voorinspectieOpmerkingen: string | null;
}

export interface OrderItemGroup {
  primary: OrderItem;
  extras: OrderItem[];
}

export interface OrderAddress {
  formatted: string;
  street: string;
  postcode: string;
  city: string;
  toevoeging?: string;
  override?: { enabled: boolean; bron: string };
}

export interface OrderAppointment {
  label: string;
  vanaf: string | null;
  tot: string | null;
  datum: string | null;
  /** Definitief tijdvenster (alleen gevuld na route-lock). Leeg = nog geen ETA mogelijk. */
  definitief: string | null;
  isDefinitief: boolean;
  /** Rit staat gepland, maar de route is nog niet gelockt — dag bekend, tijdvenster nog niet. */
  wachtOpRouteLock?: boolean;
  /** Actieve geplande rit valt buiten het normale postcodegebied (override). */
  buitenDienstgebied?: boolean;
  /** Parent route-id (Routes__c). Klikbaar naar /route-planner?routeId=… */
  routeId: string | null;
  /** Stop-id (Geplande_Rit__c.Id) — vereist voor losse afspraak-annulering. */
  ritId: string | null;
  /** Stop-status (Geplande_Rit__c.Status__c) — bv. 'Ingepland', 'Verwerkt'. */
  routeStopStatus: string | null;
  /** Medewerker die de route rijdt — alleen gevuld bij routeStopStatus='Verwerkt'. */
  medewerker: { id: string; name: string } | null;
}

/** Een geregistreerde mislukte rit-poging (rit overgeslagen: klant niet thuis e.d.). */
export interface OrderFailedAttempt {
  /** Reden overslaan, bv. 'Niet thuis', 'Geen toegang', 'Niet langs geweest'. */
  reden: string | null;
  /** Datum van de gemiste rit (ISO date). */
  datum: string | null;
  /** Parent route (Routes__c) van de overgeslagen stop — voor "Toch gelukt?". */
  routeId?: string | null;
  /** De overgeslagen stop zelf (Geplande_Rit__c.Id) — voor "Toch gelukt?". */
  ritId?: string | null;
}

export interface OrderCustomerContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  mobile: string;
  title: string;
}

export interface OrderCustomer {
  accountId: string;
  name: string;
  email: string;
  phone: string;
  mobile: string;
  /** true = PersonAccount (particulier); dan is contactpersoon-keuze niet relevant. */
  isPersonAccount: boolean;
  contact: OrderCustomerContact | null;
}

export interface AccountContact {
  id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
}

export interface OrderInvoice {
  id: string;
  name: string;
  totalInclBtw: number;
  openAmount: number;
  paid: boolean;
  /** Som van de creditfacturen op deze factuur (positief bedrag). */
  creditedAmount?: number;
  /** Volledig gecrediteerd — de order mag vrijgegeven worden voor een nieuwe factuur. */
  fullyCredited?: boolean;
}

/**
 * Factuur-overschrijvingen op de WO (Werk_Order__c).
 *  - andereNaam=false → de factuur gebruikt de accountnaam (standaard)
 *  - andereNaam=true  → de factuur gebruikt `naam`
 *  - opmerkingen      → vrije opmerkingenregel op de factuur
 *  - email            → ontvanger vóór factuur-aanmaak (Factuur_E_mail__c);
 *                       leeg = Account/Contact-default. Voorkomt dat de auto-send
 *                       naar het verkeerde adres gaat.
 */
export interface OrderFactuur {
  andereNaam: boolean;
  naam: string;
  opmerkingen: string;
  email: string;
}

export interface OrderTotals {
  totalInclBtw: number;
  paidInclBtw: number;
  openAmount: number;
  paymentMethod: string;
  paymentReceived: boolean;
  molliePaymentUrl: string | null;
  opFactuur?: boolean;
  opFactuurBron?: string;
  /**
   * De echte factuur-guard (`Gefactureerd__c`). Gebruik deze en niet de
   * aanwezigheid van `invoice`: na een vrijgave voor herfacturatie hangt de
   * oude factuur weer aan de order terwijl deze vlag false blijft.
   */
  gefactureerd?: boolean;
}

export interface OrderNotes {
  pickup: string;
  delivery: string;
  planning: string;
}

export interface OrderAddresses {
  pickup: OrderAddress;
  delivery: OrderAddress;
  shipping: OrderAddress;
}

export interface UpsellStatusEntry {
  ordered: boolean;
  bron: string;
}

export interface OrderUpsellStatus {
  ondertapijt: UpsellStatusEntry;
  vlekkenbaas: UpsellStatusEntry;
  randReparatie: UpsellStatusEntry;
}

export interface OrderDetail {
  id: string;
  orderNumber: string;
  brand: string;
  segment: string;
  recordType: string;
  status: string;
  statusLabel: string;
  createdDate: string;
  lastModifiedDate: string;
  customer: OrderCustomer;
  addresses: OrderAddresses;
  pickupAppointment: OrderAppointment | null;
  deliveryAppointment: OrderAppointment | null;
  /** Laatste mislukte poging (rit overgeslagen: klant niet thuis e.d.). Toont een
   *  "vorige poging niet gelukt"-melding onder de lege afspraak-sectie. */
  pickupLastFailedAttempt?: OrderFailedAttempt | null;
  deliveryLastFailedAttempt?: OrderFailedAttempt | null;
  items: OrderItemGroup[];
  /** Losse, klant-eigen items (Niet_Factureren__c) op order-niveau — niet gefactureerd. */
  klantEigenItems: OrderItem[];
  totals: OrderTotals;
  notes: OrderNotes;
  invoice: OrderInvoice | null;
  /** Factuur-naam/opmerkingen-overschrijvingen op de WO. */
  factuur?: OrderFactuur;
  upsellStatus?: OrderUpsellStatus;
  transacties?: TransactieRecord[];
  pickupVanafDatum: string | null;
  deliveryVanafDatum: string | null;
  datumWindowBron: string | null;
  opslagkostenStartDatum: string | null;
  geenOpslagkosten: boolean;
  redenGeenOpslagkosten: string | null;
  /** SF Id van de bovenliggende klacht — gevuld als deze WO een klacht heeft. */
  klachtParentId?: string | null;
  /** Klacht-nummer (bv. K-1234) ter weergave op de WO-kaart/badge. */
  klachtParentNummer?: string | null;
  /** Status van de bovenliggende klacht (Geregistreerd / In_Behandeling / etc.). */
  klachtStatus?: string | null;
  /** True als deze WO een vervolg-werkorder is van een eerdere klacht. */
  isVervolgWo?: boolean;
  /**
   * Klachten die óp deze order zijn geregistreerd (omgekeerde richting van
   * klachtParentId, dat zegt dat deze WO zélf een herbehandeling is). Nieuwste
   * eerst. Zonder deze lijst toont de orderpagina 'Klacht registreren' terwijl
   * er al een open klacht ligt.
   */
  klachtenOpOrder?: KlachtOpOrder[];
  /** Klant haalt kleed zelf op na reiniging — geen aflever-rit. */
  selfPickup?: boolean;
  /** Klant brengt kleed zelf naar Havelte — geen ophaal-rit. */
  selfDelivery?: boolean;
  /** Moment waarop de klant de kleden zelf langsbracht (Zelfservice_Ontvangen_op__c). */
  selfDeliveredAt?: string | null;
  /** Moment waarop de klant de schone kleden zelf ophaalde (Zelfservice_Opgehaald_op__c). */
  selfPickedUpAt?: string | null;
  /** Bevestigingen-toggle (S01/S03). False = uitgesteld; klik "Nu versturen" om alsnog te activeren. */
  confirmationsEnabled?: boolean;
  /** True als de WO email mag versturen (Contact heeft email + niet bounced). Uit Werk_Order__c.Email_Mag_Verzonden__c. */
  emailMagVerzonden?: boolean;
  /** Ophaal-reminder-state — voedt OphaalReminderCard op detail-page. */
  reminderState?: {
    inSequence: boolean;
    count: number;
    emailMagVerzonden: boolean;
    bounced: boolean;
    startDate: string | null;
    laatsteKansDate: string | null;
    telefoonTaskCreated: boolean;
    redenAnnulering: string | null;
    reactiveringBron: string | null;
  };
  /**
   * Reminder-pauze-state (klant op vakantie) per spoor — voedt de
   * "Reminders pauzeren tot…"-actie + badge op de detail-page. Pauzevelden
   * zijn 'YYYY-MM-DD' of null (geen pauze).
   */
  reminderSnooze?: {
    afleverPauzeTot: string | null;
    ophaalPauzeTot: string | null;
    inAfleverSequence: boolean;
    inOphaalSequence: boolean;
  };
  /** RecordType.DeveloperName — bv. 'Wasserij', 'Webshop', 'SofaSaver', 'Reparatie'. */
  recordTypeDeveloperName?: string;
  /** Operationele vlaggen — server-driven kanonieke shape. */
  flags?: {
    geenOpslagkosten: boolean;
    zelfBrengen: boolean;
    zelfHalen: boolean;
    spoed: boolean;
    geenMinimumTariefWederverkoper: boolean;
    /** Wanneer false onderdrukt de WO-flow alle bevestigings-/plannings-emails. */
    bevestigingenSturen: boolean;
    /** Geen automatisch ophaalvoorstel/-reminders — order valt buiten het routesysteem, plan handmatig. */
    ophalenHandmatigInplannen: boolean;
    /** Geen automatisch aflevervoorstel/-reminders — order valt buiten het routesysteem, plan handmatig. */
    afleverenHandmatigInplannen: boolean;
  };
  /** Welke flags zijn UI-relevant voor deze order (bv. wederverkoper-only). */
  flagsApplicable?: {
    geenMinimumTariefWederverkoper: boolean;
  };
  /** Vlag staat uit maar het effect geldt toch, afgeleid (wederverkoper + zelf brengen + zelf halen). */
  flagsAfgeleid?: {
    geenMinimumTariefWederverkoper: boolean;
  };
  /** Statussen die office-app via update-fields mag zetten — server-side whitelist. */
  availableStatuses?: string[];
  /**
   * Werk_Order__c.Route_Keuze_Token__c — magic-link-token voor de client-app.
   * Bouw `https://client.kleda.nl/?token=<x>&view=voorinspectie` om de klant-view
   * te openen zonder login. Null als token nog niet gegenereerd is.
   */
  clientAppToken?: string | null;
  /**
   * Actieve order-korting uit `Werk_Order__c.Actie_Code__c`.
   *  - kind='none' → geen korting (klikken om er een toe te voegen)
   *  - kind='manual' → ad-hoc KORTING:X — wijzigbaar in office-app
   *  - kind='campaign' → geregistreerde campagne-code (bv. 'lente2026') — niet
   *    wijzigbaar via deze flow, callcenter moet eerst de campagne wissen
   * `totalAmountInclBtw` is de rollup uit `Totale_Korting_incl_BTW__c`.
   */
  discount?: OrderDiscount;
  /** Automatische bundelkorting uitgesloten (B2B opt-out). */
  geenBundelkorting?: boolean;
}

export type OrderDiscount =
  | { kind: 'none'; percentage: null; code: null; totalAmountInclBtw: number }
  | { kind: 'manual'; percentage: number | null; code: string; totalAmountInclBtw: number }
  | { kind: 'toeslag'; percentage: number | null; code: string; totalAmountInclBtw: number }
  | { kind: 'campaign'; percentage: null; code: string; totalAmountInclBtw: number };

export type ItemChangeKind = 'add' | 'update' | 'remove';

export type ItemShape = 'rechthoekig' | 'rond' | 'overig';

export interface ItemChange {
  kind: ItemChangeKind;
  itemId?: string;
  vorm?: ItemShape;
  lengteCm?: number;
  breedteCm?: number;
  diameterCm?: number;
  oppervlakteM2?: number;
  primarySku?: string;
  packageSku?: string;
  ondertapijt?: boolean;
  /** Alleen per-stuk primaries (Kussen); weggelaten = 1. */
  aantal?: number;
}

export interface PricingPreviewPerItem {
  itemId: string | null;
  label: string;
  oldPriceInclBtw: number;
  newPriceInclBtw: number;
}

export interface BundelkortingInfo {
  applicable: boolean;
  percentage: number;
  bedrag: number;
}

export interface PricingPreview {
  oldTotalInclBtw: number;
  newTotalInclBtw: number;
  diffInclBtw: number;
  perItem: PricingPreviewPerItem[];
  bundelkorting: BundelkortingInfo | null;
  kortingTotaalInclBtw?: number;
}

export interface BulkUpdateResult {
  oldTotalInclBtw: number;
  newTotalInclBtw: number;
  diffInclBtw: number;
  nieuweLastModifiedDate: string | null;
  bundelkorting: BundelkortingInfo;
}

export interface CancelResult {
  nieuweStatus: string;
  nieuweLastModifiedDate: string | null;
}

export type AuditStatus = 'pending' | 'success' | 'error' | 'geweigerd' | 'schaduw';

export interface AuditLogEntry {
  id: string;
  action: string;
  actorEmail: string;
  status: AuditStatus;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  /** Kanaal van de wijziging: office-app, client-app, voice-agent, field-app, brein, systeem. */
  channel: string;
  /** Leesbare regel van de schrijver; null bij oude rijen (val terug op action-label). */
  samenvatting: string | null;
  /** Namens wie gehandeld is (voice-agent handelt namens de beller). */
  onBehalfOf: string | null;
}

export class OrderConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OrderConflictError';
  }
}

export type DeletionBlockCode =
  | 'BLOCKED_AFGELEVERD'
  | 'BLOCKED_ROUTE_VERWERKT'
  | 'BLOCKED_BETALING'
  | 'BLOCKED_KLACHT'
  | 'BLOCKED_VERZONDEN';

export interface OrderDeletionPreview {
  canDelete: boolean;
  blockReason: string | null;
  blockCode: DeletionBlockCode | null;
  aantalWasserijItems: number;
  aantalRitten: number;
  werkOrderNummer: string;
  huidigeStatus: string;
  /** Neveneffecten buiten SF die het verwijderen niet opruimt (MyParcel, Mollie). */
  waarschuwingen: string[];
}

export interface OrderDeletionResult {
  verwijderingsRecordId: string;
  aantalWasserijItems: number;
  aantalRitten: number;
}

export class OrderDeletionBlockedError extends Error {
  blockCode: DeletionBlockCode;
  constructor(blockCode: DeletionBlockCode, message: string) {
    super(message);
    this.name = 'OrderDeletionBlockedError';
    this.blockCode = blockCode;
  }
}

export interface OrderListItem {
  id: string;
  orderNumber: string;
  customerName: string;
  email: string;
  postcode: string;
  city: string;
  brand: string;
  recordType: string;
  recordTypeDeveloperName?: string;
  status: string;
  totalInclBtw: number;
  openAmount: number;
  paymentReceived: boolean;
  createdDate: string;
  lastModifiedDate: string;
  /** Klacht-info (zie OrderDetail). */
  klachtParentId?: string | null;
  klachtParentNummer?: string | null;
  klachtStatus?: string | null;
  isVervolgWo?: boolean;
  /** Opdracht zonder bereikbaar telefoonnummer (SF formule Telefoon_Ontbreekt__c). */
  telefoonOntbreekt?: boolean;
  /** Ophaal-reminder-state — voedt badge op tegel en filter "actie vereist". */
  reminderState?: {
    inSequence: boolean;
    count: number;
    emailMagVerzonden: boolean;
    bounced: boolean;
    startDate: string | null;
    laatsteKansDate: string | null;
    redenAnnulering: string | null;
  };
}

export interface OrderListFilters {
  /** Brand__c picklist value (KLEDA / CINCO / SCHOONHUISSHOP / SOFASAVER). */
  brand?: string;
  /** Werk_Order_Status__c picklist value. */
  status?: string;
  /** RecordType.DeveloperName (Wasserij / Webshop / Uitvoering / ...). */
  recordType?: string;
  /** Datum-window op CreatedDate. Default '90'. */
  days?: '30' | '90' | '365' | 'all';
  /** Toont alleen WO's met openstaande reminder-actie (bounce of count≥3). */
  reminderActieVereist?: boolean;
  /** Toont alleen opdrachten zonder bereikbaar telefoonnummer (opvolglijst). */
  telefoonOntbreekt?: boolean;
}

export interface OrderRecordTypeOption {
  developerName: string;
  name: string;
}

export interface OrderListResponse {
  orders: OrderListItem[];
  availableRecordTypes: OrderRecordTypeOption[];
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getToken();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

/**
 * Gelijktijdige aanroepen voor dezelfde order delen één request. De
 * order-drawer laadde `orders/detail` twee keer bij openen (rondgang 14-09);
 * na een mutatie volgt de refetch later en apart, die blijft vers.
 */
const orderDetailInFlight = new Map<string, Promise<OrderDetail>>();

export function fetchOrderDetail(id: string): Promise<OrderDetail> {
  const lopend = orderDetailInFlight.get(id);
  if (lopend) return lopend;
  const p = (async () => {
    const headers = await authHeaders();
    const response = await fetch(`/api/orders/detail?id=${encodeURIComponent(id)}`, { headers });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Request failed' }));
      const error = new Error(err.error || 'Request failed') as Error & { status?: number };
      error.status = response.status;
      throw error;
    }
    return response.json() as Promise<OrderDetail>;
  })();
  orderDetailInFlight.set(id, p);
  p.finally(() => { if (orderDetailInFlight.get(id) === p) orderDetailInFlight.delete(id); }).catch(() => {});
  return p;
}

export interface CascadeBehandeling {
  id: string;
  name: string;
  priceInclBtw: number;
}

export interface CascadeRitImpact {
  ritId: string;
  type: 'heen' | 'terug';
  datum: string | null;
  wordtLeegNaDelete: boolean;
}

export interface ItemCascadePreview {
  primary: { id: string; name: string; priceInclBtw: number };
  behandelingen: CascadeBehandeling[];
  reparateurRits: CascadeRitImpact[];
  totaalImpactInclBtw: number;
  isInvoiced: boolean;
}

export async function fetchItemCascadePreview(wiId: string): Promise<ItemCascadePreview> {
  const headers = await authHeaders();
  const response = await fetch(
    `/api/orders/items-cascade-preview?id=${encodeURIComponent(wiId)}`,
    { headers }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Cascade-preview mislukt' }));
    throw new Error(err.error || 'Cascade-preview mislukt');
  }
  return response.json();
}

export async function fetchOrderList(
  filters?: OrderListFilters
): Promise<OrderListResponse> {
  const headers = await authHeaders();
  const params = new URLSearchParams();
  if (filters?.brand) params.set('brand', filters.brand);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.recordType) params.set('recordType', filters.recordType);
  if (filters?.days) params.set('days', filters.days);
  if (filters?.reminderActieVereist) params.set('reminderActieVereist', 'true');
  if (filters?.telefoonOntbreekt) params.set('telefoonOntbreekt', 'true');
  const qs = params.toString();
  const response = await fetch(`/api/orders/list${qs ? `?${qs}` : ''}`, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Kon orders niet laden');
  }
  const payload = await response.json() as Partial<OrderListResponse>;
  return {
    orders: Array.isArray(payload.orders) ? payload.orders : [],
    availableRecordTypes: Array.isArray(payload.availableRecordTypes)
      ? payload.availableRecordTypes
      : [],
  };
}

export async function searchOrders(
  query: string,
  signal?: AbortSignal
): Promise<OrderListItem[]> {
  const headers = await authHeaders();
  const response = await fetch(
    `/api/orders/search?q=${encodeURIComponent(query)}`,
    { headers, signal }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Zoeken mislukt');
  }
  const payload = await response.json() as { orders?: OrderListItem[] };
  return Array.isArray(payload.orders) ? payload.orders : [];
}

async function handleMutationError(response: Response, fallbackMsg: string): Promise<never> {
  const err = await response.json().catch(() => ({ error: fallbackMsg }));
  const msg = err.error || fallbackMsg;
  // Alleen een échte concurrency-botsing is een OrderConflictError. Een 409 die
  // een andere code draagt (WO_GEANNULEERD, ALREADY_FREE, STATUS_GUARD, …) is
  // een toestandsweigering met een eigen melding; die als conflict behandelen
  // verving de servertekst door "Order is ondertussen gewijzigd" en triggerde
  // een refetch, waardoor de UI leek te bevriezen op de oude waarde.
  const isConcurrencyConflict =
    err.code === 'CONFLICT' || (response.status === 409 && !err.code);
  if (isConcurrencyConflict) {
    throw new OrderConflictError(msg);
  }
  const e = new Error(msg) as Error & { status?: number; code?: string };
  e.status = response.status;
  e.code = err.code;
  throw e;
}

export interface ToggleOpFactuurResult {
  werkOrderId: string;
  werkOrderNummer: string | null;
  opFactuur: boolean;
  lastModifiedDate: string | null;
  message: string | null;
}

export async function toggleOpFactuur(params: {
  werkOrderId: string;
  opFactuur: boolean;
  reden: string;
}): Promise<ToggleOpFactuurResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/op-factuur', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      werkOrderId: params.werkOrderId,
      op_factuur: params.opFactuur,
      reden: params.reden,
    }),
  });
  if (!response.ok) return handleMutationError(response, 'Op factuur-toggle mislukt');
  const payload = await response.json();
  return {
    werkOrderId: payload.werkOrderId ?? params.werkOrderId,
    werkOrderNummer: payload.werkOrderNummer ?? null,
    opFactuur: payload.opFactuur === true,
    lastModifiedDate: payload.lastModifiedDate ?? null,
    message: payload.message ?? null,
  };
}

export async function cancelOrder(
  orderId: string,
  reden: string,
  expectedLastModifiedDate?: string
): Promise<CancelResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/cancel', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, reden, expectedLastModifiedDate }),
  });
  if (!response.ok) return handleMutationError(response, 'Annuleren mislukt');
  const payload = await response.json();
  return {
    nieuweStatus: payload.nieuweStatus ?? 'Geannuleerd',
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
  };
}

export interface SelfServiceCompleteResult {
  /** Nieuwe WO-status: 'Afgerond' (opgehaald) of 'Zelf gebracht' (gebracht). */
  newStatus: string;
  /** Geregistreerde datum/tijd (ISO) zoals door de backend gestempeld. */
  completedAt: string | null;
}

/**
 * Registreert dat een zelf-halen/brengen-order is opgehaald ('Wordt opgehaald'
 * → 'Afgerond') of gebracht ('Wordt gebracht' → 'Zelf gebracht'). Optioneel met
 * een afwijkende registratiedatum (`completedAt`, yyyy-MM-dd); leeg = vandaag/nu.
 * Spiegelt de field-app — backend zet status, wasserij-items én de datum.
 */
export async function completeSelfService(
  workOrderId: string,
  completedAt?: string
): Promise<SelfServiceCompleteResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/self-service-complete', {
    method: 'POST',
    headers,
    body: JSON.stringify({ workOrderId, completedAt }),
  });
  if (!response.ok) return handleMutationError(response, 'Registreren mislukt');
  const payload = await response.json();
  return {
    newStatus: payload.newStatus ?? '',
    completedAt: payload.completedAt ?? null,
  };
}

export interface MaakGratisResult {
  nieuweStatus: string;
  nieuweTotaalInclBtw: number;
  itemsZeroed: number;
}

/**
 * Maakt een Webshop-order alsnog gratis (€0) door op elke regel een 100%
 * handmatige korting te zetten. Vangnet voor een per ongeluk doorberekende
 * nalevering — voor Wasserij-orders gebruik je de KortingPill. Server-side
 * geguard op recordtype (Webshop), niet-betaald en niet-geannuleerd.
 */
export async function maakWebshopOrderGratis(orderId: string): Promise<MaakGratisResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/webshop/maak-gratis', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId }),
  });
  if (!response.ok) return handleMutationError(response, 'Gratis maken mislukt');
  const payload = await response.json();
  return {
    nieuweStatus: payload.nieuweStatus ?? '',
    nieuweTotaalInclBtw: payload.nieuweTotaalInclBtw ?? 0,
    itemsZeroed: payload.itemsZeroed ?? 0,
  };
}

export interface ReactivateResult {
  nieuweStatus: string;
  nieuweLastModifiedDate: string | null;
  vorigeReden: string | null;
}

/**
 * Herstelt ("un-cancel") een geannuleerde Wasserij-order: status terug naar
 * 'Ophalen plannen', verse reminder-sequence, annulering-velden gewist. Alleen
 * admin|medewerker (server-side role-gate). Reden van de oude annulering komt
 * terug als `vorigeReden` voor weergave/audit.
 */
export async function reactivateOrder(
  orderId: string,
  expectedLastModifiedDate?: string
): Promise<ReactivateResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/reactivate', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, expectedLastModifiedDate }),
  });
  if (!response.ok) return handleMutationError(response, 'Herstellen mislukt');
  const payload = await response.json();
  return {
    nieuweStatus: payload.nieuweStatus ?? 'Ophalen plannen',
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
    vorigeReden: payload.vorigeReden ?? null,
  };
}

export interface CancelAppointmentParams {
  orderId: string;
  ritId: string;
  fase: 'pickup' | 'delivery';
  reden: string;
  /** Default true. Op false geeft de server een admin-only recovery-pad door waarmee ook `Verwerkt`-ritten geannuleerd kunnen worden. */
  enforceStatusGuard?: boolean;
}

export interface CancelAppointmentResult {
  ritId: string;
  nieuweStatus: string;
  ritLastModifiedDate: string | null;
  message: string | null;
}

export async function cancelAppointment(
  params: CancelAppointmentParams
): Promise<CancelAppointmentResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/appointment-cancel', {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });
  if (!response.ok) return handleMutationError(response, 'Afspraak annuleren mislukt');
  const payload = await response.json();
  return {
    ritId: payload.ritId ?? params.ritId,
    nieuweStatus: payload.nieuweStatus ?? 'Geannuleerd',
    ritLastModifiedDate: payload.ritLastModifiedDate ?? null,
    message: payload.message ?? null,
  };
}

export async function bulkUpdateOrder(
  orderId: string,
  changes: ItemChange[],
  expectedLastModifiedDate?: string
): Promise<BulkUpdateResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/bulk-update', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, itemChanges: changes, expectedLastModifiedDate }),
  });
  if (!response.ok) return handleMutationError(response, 'Opslaan mislukt');
  const payload = await response.json();
  return {
    oldTotalInclBtw: payload.oldTotalInclBtw ?? 0,
    newTotalInclBtw: payload.newTotalInclBtw ?? 0,
    diffInclBtw: payload.diffInclBtw ?? 0,
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
    bundelkorting: payload.bundelkorting ?? { applicable: false, percentage: 0, bedrag: 0 },
  };
}

export interface UpdateNotesResult {
  nieuweLastModifiedDate: string | null;
}

export async function updateOrderNotes(
  orderId: string,
  notes: { pickupNotes?: string | null; deliveryNotes?: string | null },
  expectedNotes: { pickupNotes?: string | null; deliveryNotes?: string | null },
  expectedLastModifiedDate?: string
): Promise<UpdateNotesResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/update-notes', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, ...notes, expectedPickupNotes: expectedNotes.pickupNotes,
      expectedDeliveryNotes: expectedNotes.deliveryNotes, expectedLastModifiedDate }),
  });
  if (!response.ok) return handleMutationError(response, 'Opslaan mislukt');
  const payload = await response.json();
  return {
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
  };
}

export interface AppendPlanningNotesResult {
  nieuweInhoud: string;
  nieuweLastModifiedDate: string | null;
}

export async function appendPlanningNotes(
  orderId: string,
  entry: string,
  context: string = 'Office'
): Promise<AppendPlanningNotesResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/planning-notes-append', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, entry, context }),
  });
  if (!response.ok) return handleMutationError(response, 'Toevoegen mislukt');
  const payload = await response.json();
  return {
    nieuweInhoud: payload.nieuweInhoud ?? '',
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
  };
}

export interface OrderFieldMutation {
  newStatus?: string | null;
  flags?: {
    geenOpslagkosten?: boolean | null;
    zelfBrengen?: boolean | null;
    zelfHalen?: boolean | null;
    spoed?: boolean | null;
    geenMinimumTariefWederverkoper?: boolean | null;
    bevestigingenSturen?: boolean | null;
    ophalenHandmatigInplannen?: boolean | null;
    afleverenHandmatigInplannen?: boolean | null;
  };
  /** Factuur-overschrijvingen — alleen meegestuurde keys worden gemuteerd. */
  invoice?: {
    andereNaam?: boolean | null;
    naam?: string | null;
    opmerkingen?: string | null;
    email?: string | null;
  };
}

export interface UpdateFieldsResult {
  nieuweStatus: string | null;
  nieuweLastModifiedDate: string | null;
  appliedFields: string[];
  noChanges: boolean;
}

export async function updateOrderFields(
  orderId: string,
  mutation: OrderFieldMutation,
  expectedLastModifiedDate?: string
): Promise<UpdateFieldsResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/update-fields', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      orderId,
      newStatus: mutation.newStatus ?? undefined,
      flags: mutation.flags,
      invoice: mutation.invoice,
      expectedLastModifiedDate,
    }),
  });
  if (!response.ok) return handleMutationError(response, 'Bijwerken mislukt');
  const payload = await response.json();
  return {
    nieuweStatus: payload.nieuweStatus ?? null,
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
    appliedFields: Array.isArray(payload.appliedFields) ? payload.appliedFields : [],
    noChanges: !!payload.noChanges,
  };
}

export interface UpdateContactResult {
  nieuweLastModifiedDate: string | null;
  contact: OrderCustomerContact | null;
  noChanges: boolean;
}

/**
 * Haalt de contactpersonen op die aan een account hangen (max 50).
 * Bron: /api/customers/contacts-by-account.
 */
export async function fetchAccountContacts(accountId: string): Promise<AccountContact[]> {
  const headers = await authHeaders();
  const response = await fetch(
    `/api/customers/contacts-by-account?id=${encodeURIComponent(accountId)}`,
    { headers }
  );
  if (!response.ok) return handleMutationError(response, 'Contactpersonen laden mislukt');
  const payload = await response.json();
  const contacts = Array.isArray(payload.contacts) ? payload.contacts : [];
  return contacts.map((c: Record<string, unknown>) => ({
    id: String(c.id ?? ''),
    name: String(c.name ?? ''),
    email: String(c.email ?? ''),
    phone: String(c.phone ?? ''),
    title: String(c.title ?? ''),
  }));
}

/**
 * Wijzigt de contactpersoon (Contactpersoon__c) van een werk order.
 * `contactId = null` wist de contactpersoon.
 */
export async function updateOrderContact(
  orderId: string,
  contactId: string | null,
  expectedLastModifiedDate?: string
): Promise<UpdateContactResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/update-contact', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, contactId, expectedLastModifiedDate }),
  });
  if (!response.ok) return handleMutationError(response, 'Contactpersoon wijzigen mislukt');
  const payload = await response.json();
  return {
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
    contact: payload.contact ?? null,
    noChanges: !!payload.noChanges,
  };
}

export async function setOrderConfirmations(
  orderId: string,
  enable: boolean,
  expectedLastModifiedDate?: string
): Promise<UpdateNotesResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/set-confirmations', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, enable, expectedLastModifiedDate }),
  });
  if (!response.ok) return handleMutationError(response, 'Bijwerken mislukt');
  const payload = await response.json();
  return {
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
  };
}

export interface UpdateWasserijItemMetaResult {
  nieuweLastModifiedDate: string | null;
  updatedFields: string[];
}

export async function updateWasserijItemMeta(
  itemId: string,
  patch: Record<string, unknown>
): Promise<UpdateWasserijItemMetaResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/wasserij-item-update', {
    method: 'POST',
    headers,
    body: JSON.stringify({ itemId, patch }),
  });
  if (!response.ok) return handleMutationError(response, 'Opslaan mislukt');
  const payload = await response.json();
  return {
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
    updatedFields: Array.isArray(payload.updatedFields) ? payload.updatedFields : [],
  };
}

/**
 * Zet (of wist) een handmatige korting op één wasserij-item-regel — bedoeld om
 * een behandeling of kleed gratis (100%) of afgeprijsd (bijv. 50%) te geven.
 *
 * Mechaniek (geverifieerd tegen prod): de getoonde regelprijs `Totaal_incl_BTW__c`
 * is een formule die `Korting__c` als fractie verrekent, dus 100 → €0. Maar de
 * order-korting cascadeert via PricingService over alle items en zou een kale
 * `Korting__c` bij de eerstvolgende reprice overschrijven. `Andere_prijs__c=true`
 * markeert de korting als handmatig (`hasManualDiscount`) zodat die behouden
 * blijft — hetzelfde patroon als NewWerkOrderActionHandler/RisicoMeldingService.
 *
 * @param kortingPct 1–100 (100 = gratis). `null` of `0` wist de handmatige korting
 *   en laat het item terugvallen op de order-cascade.
 */
export async function setWasserijItemKorting(
  itemId: string,
  kortingPct: number | null
): Promise<UpdateWasserijItemMetaResult> {
  const pct =
    kortingPct == null ? null : Math.round(Math.max(0, Math.min(100, kortingPct)) * 100) / 100;
  if (pct == null || pct === 0) {
    return updateWasserijItemMeta(itemId, { Andere_prijs__c: false, Korting__c: null });
  }
  return updateWasserijItemMeta(itemId, { Andere_prijs__c: true, Korting__c: pct });
}

export interface RemoveWasserijItemExtraResult {
  newTotalInclBtw: number | null;
}

export async function removeWasserijItemExtra(
  childItemId: string
): Promise<RemoveWasserijItemExtraResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/wasserij-item-extra-remove', {
    method: 'POST',
    headers,
    body: JSON.stringify({ childItemId }),
  });
  if (!response.ok) return handleMutationError(response, 'Verwijderen mislukt');
  const payload = await response.json();
  return {
    newTotalInclBtw: payload.newTotalInclBtw ?? null,
  };
}

export async function fetchPricingPreview(
  orderId: string,
  changes: ItemChange[]
): Promise<PricingPreview> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/pricing-preview', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, itemChanges: changes }),
  });
  if (!response.ok) return handleMutationError(response, 'Prijs-preview mislukt');
  const payload = await response.json();
  return {
    oldTotalInclBtw: payload.oldTotalInclBtw ?? 0,
    newTotalInclBtw: payload.newTotalInclBtw ?? 0,
    diffInclBtw: payload.diffInclBtw ?? 0,
    perItem: payload.perItem ?? [],
    bundelkorting: payload.bundelkorting ?? null,
    kortingTotaalInclBtw: payload.kortingTotaalInclBtw,
  };
}

export interface AddressOverrideSide {
  enable: boolean;
  street?: string;
  postcode?: string;
  city?: string;
  toevoeging?: string;
}

export interface AddressOverrideResult {
  pickupApplied: boolean;
  deliveryApplied: boolean;
  nieuweLastModifiedDate: string | null;
  noChanges: boolean;
}

export async function applyAddressOverride(
  orderId: string,
  sides: { pickup?: AddressOverrideSide; delivery?: AddressOverrideSide }
): Promise<AddressOverrideResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/address-override', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, ...sides }),
  });
  if (!response.ok) return handleMutationError(response, 'Adres-override mislukt');
  const payload = await response.json();
  return {
    pickupApplied: !!payload.pickupApplied,
    deliveryApplied: !!payload.deliveryApplied,
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
    noChanges: !!payload.noChanges,
  };
}

export interface DatumWindowSide {
  enable: boolean;
  datum?: string | null;
}

export interface DatumWindowResult {
  pickupApplied: boolean;
  deliveryApplied: boolean;
  nieuweLastModifiedDate: string | null;
  noChanges: boolean;
}

export interface SetOrderDiscountResult {
  appliedPercentage: number;
  nieuweTotaalInclBtw: number | null;
  nieuweTotaleKortingInclBtw: number | null;
  nieuweLastModifiedDate: string | null;
  noChanges: boolean;
  /** Echo van het gevraagde vaste bedrag, of null bij een percentage-mutatie. */
  doelBedragInclBtw: number | null;
  /** False als het vaste bedrag niet exact haalbaar bleek. */
  doelGehaald: boolean;
  /** Gevuld als zelfs 100% korting niet onder dit bedrag komt. */
  laagsteHaalbaarInclBtw: number | null;
  /** Melding voor de medewerker, bv. dat de betaallink niet mee kon. */
  melding: string | null;
}

/**
 * Stel een handmatige order-korting in via Actie_Code__c='KORTING:<pct>'.
 * Geef `percentage=null` of `0` om de korting te verwijderen. Met
 * `overrideCampaign=true` mag een geregistreerde campagne-code (bv.
 * `lente2026`) worden vervangen door deze handmatige korting.
 *
 * Met `targetAmountInclBtw` geef je in plaats van een percentage het bedrag op
 * dat met de klant is afgesproken (incl. BTW); de server rekent het percentage
 * erbij en meldt terug waar het op landde.
 */
export async function setOrderDiscount(
  orderId: string,
  percentage: number | null,
  options?: {
    overrideCampaign?: boolean;
    type?: 'korting' | 'toeslag';
    /** Verplicht bij toepassen (percentage > 0), optioneel bij verwijderen. */
    reason?: DiscountReason;
    reasonNote?: string;
    /** Afgesproken eindbedrag incl. BTW — vervangt `percentage`. */
    targetAmountInclBtw?: number;
    /** Kortingsbedrag incl. BTW dat van de huidige prijs af gaat — vervangt `percentage`. */
    discountAmountInclBtw?: number;
  }
): Promise<SetOrderDiscountResult> {
  const headers = await authHeaders();
  const vastBedrag = options?.targetAmountInclBtw;
  const kortingsbedrag = options?.discountAmountInclBtw;
  const response = await fetch('/api/orders/discount', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      orderId,
      // Bij een vast bedrag mag `percentage` niet mee: de server weigert beide.
      ...(vastBedrag != null
        ? { targetAmountInclBtw: vastBedrag }
        : kortingsbedrag != null
          ? { discountAmountInclBtw: kortingsbedrag }
          : { percentage }),
      overrideCampaign: options?.overrideCampaign === true,
      ...(options?.type ? { type: options.type } : {}),
      ...(options?.reason ? { reason: options.reason } : {}),
      ...(options?.reasonNote ? { reasonNote: options.reasonNote } : {}),
    }),
  });
  if (!response.ok) return handleMutationError(response, 'Korting wijzigen mislukt');
  const payload = await response.json();
  return {
    appliedPercentage: payload.appliedPercentage ?? 0,
    nieuweTotaalInclBtw: payload.nieuweTotaalInclBtw ?? null,
    nieuweTotaleKortingInclBtw: payload.nieuweTotaleKortingInclBtw ?? null,
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
    noChanges: !!payload.noChanges,
    doelBedragInclBtw: payload.doelBedragInclBtw ?? null,
    doelGehaald: payload.doelGehaald !== false,
    laagsteHaalbaarInclBtw: payload.laagsteHaalbaarInclBtw ?? null,
    melding: payload.melding ?? null,
  };
}

export async function setOrderDatumWindow(
  orderId: string,
  sides: { pickup?: DatumWindowSide; delivery?: DatumWindowSide }
): Promise<DatumWindowResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/datum-window', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, ...sides }),
  });
  if (!response.ok) return handleMutationError(response, 'Datum-window mutatie mislukt');
  const payload = await response.json();
  return {
    pickupApplied: !!payload.pickupApplied,
    deliveryApplied: !!payload.deliveryApplied,
    nieuweLastModifiedDate: payload.nieuweLastModifiedDate ?? null,
    noChanges: !!payload.noChanges,
  };
}

// ─── Werk_Order email-templates (S00..S12 + ZS01/ZS02) ─────────────────────

/**
 * EmailTemplate.DeveloperName van de transactionele Werk_Order-templates die
 * via WerkOrderEmailService kunnen worden verstuurd. Identiek aan de
 * `ALLOWED_TEMPLATES` set in de Apex-service.
 */
export type OrderEmailStatusKey =
  | 'S00_Annulering'
  | 'S01_OpdrachtBevestiging'
  | 'S02_OphaalafspraakMaken'
  | 'S03_Ophaalbevestiging'
  | 'S05_Opgehaald'
  | 'S06_Voorinspectie'
  | 'S08_AfleverafspraakMaken'
  | 'S09_Afleverbevestiging'
  | 'S10_Tevredenheidscheck'
  | 'S12_OpdrachtAfgerond_B2B'
  | 'ZS01_WordtGebracht'
  | 'ZS02_WordtOpgehaald';

export interface OrderEmailTemplate {
  key: OrderEmailStatusKey;
  label: string;
  hint?: string;
}

export const ORDER_EMAIL_TEMPLATES: OrderEmailTemplate[] = [
  { key: 'S01_OpdrachtBevestiging', label: 'Bevestiging (S01)', hint: 'Stuur de orderbevestiging incl. wijzigingen' },
  { key: 'S02_OphaalafspraakMaken', label: 'Ophaalafspraak maken (S02)' },
  { key: 'S03_Ophaalbevestiging', label: 'Ophaalbevestiging (S03)' },
  { key: 'S05_Opgehaald', label: 'Opgehaald (S05)' },
  { key: 'S06_Voorinspectie', label: 'Voorinspectie klaar (S06)' },
  { key: 'S08_AfleverafspraakMaken', label: 'Afleverafspraak maken (S08)' },
  { key: 'S09_Afleverbevestiging', label: 'Afleverbevestiging (S09)' },
  { key: 'ZS01_WordtGebracht', label: 'Wordt gebracht (ZS01)' },
  { key: 'ZS02_WordtOpgehaald', label: 'Wordt opgehaald (ZS02)' },
  { key: 'S10_Tevredenheidscheck', label: 'Tevredenheidscheck (S10)' },
  { key: 'S12_OpdrachtAfgerond_B2B', label: 'Opdracht afgerond B2B (S12)' },
  { key: 'S00_Annulering', label: 'Annulering (S00)' },
];

/**
 * Mapping Werk_Order_Status__c → bijbehorende email-template (zelfde
 * decisions als Werk_Order_RecordTriggered_AfterSave flow). Statussen
 * zonder bijbehorende template returnen null — dan toont de UI alleen
 * S01 als primaire knop + de dropdown.
 */
export function statusToEmailKey(status: string | null | undefined): OrderEmailStatusKey | null {
  switch ((status || '').trim()) {
    case 'Ophaalvoorstel gedaan':
    case 'Ophaalvoorstel geen reactie':
      return 'S02_OphaalafspraakMaken';
    case 'Ophalen ingepland':
      return 'S03_Ophaalbevestiging';
    case 'Opgehaald':
      return 'S05_Opgehaald';
    case 'Klaar voor Voorinspectie':
    case 'Voorinspectie gedaan':
      return 'S06_Voorinspectie';
    case 'Afleveren plannen':
    case 'Aflevervoorstel gedaan':
    case 'Aflevervoorstel geen reactie':
      return 'S08_AfleverafspraakMaken';
    case 'Terugbrengen ingepland':
    case 'Afleveren ingepland':
      return 'S09_Afleverbevestiging';
    case 'Geannuleerd':
      return 'S00_Annulering';
    case 'Afgeleverd':
      return 'S10_Tevredenheidscheck';
    default:
      return null;
  }
}

export function emailTemplateLabel(key: OrderEmailStatusKey): string {
  return ORDER_EMAIL_TEMPLATES.find((t) => t.key === key)?.label || key;
}

export interface SendOrderEmailResult {
  ok: true;
  templateUsed: OrderEmailStatusKey;
  sentAt: string;
  bron: string;
}

export async function sendOrderEmail(
  orderId: string,
  statusKey: OrderEmailStatusKey
): Promise<SendOrderEmailResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/send-email', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, statusKey }),
  });
  if (!response.ok) return handleMutationError(response, 'E-mail versturen mislukt');
  const payload = await response.json();
  return {
    ok: true,
    templateUsed: (payload.templateUsed as OrderEmailStatusKey) ?? statusKey,
    sentAt: payload.sentAt ?? new Date().toISOString(),
    bron: payload.bron ?? 'Office-App',
  };
}

export async function previewDeleteOrder(orderId: string): Promise<OrderDeletionPreview> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/delete-preview', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Preview mislukt' }));
    throw new Error(err.error || 'Preview mislukt');
  }
  const payload = await response.json();
  return {
    canDelete: !!payload.canDelete,
    blockReason: payload.blockReason ?? null,
    blockCode: (payload.blockCode as DeletionBlockCode | null) ?? null,
    aantalWasserijItems: payload.aantalWasserijItems ?? 0,
    aantalRitten: payload.aantalRitten ?? 0,
    werkOrderNummer: payload.werkOrderNummer ?? '',
    huidigeStatus: payload.huidigeStatus ?? '',
    waarschuwingen: Array.isArray(payload.waarschuwingen) ? payload.waarschuwingen : [],
  };
}

export async function deleteOrder(
  orderId: string,
  reden: string,
  redenAnders?: string
): Promise<OrderDeletionResult> {
  const headers = await authHeaders();
  const response = await fetch('/api/orders/delete', {
    method: 'POST',
    headers,
    body: JSON.stringify({ orderId, reden, redenAnders }),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Verwijderen mislukt' }));
    const code = (err.code as string) || '';
    if (response.status === 409 && code.startsWith('BLOCKED_')) {
      throw new OrderDeletionBlockedError(
        code as DeletionBlockCode,
        err.error || 'Verwijderen geblokkeerd'
      );
    }
    const e = new Error(err.error || 'Verwijderen mislukt') as Error & {
      status?: number;
      code?: string;
    };
    e.status = response.status;
    e.code = code || undefined;
    throw e;
  }
  const payload = await response.json();
  return {
    verwijderingsRecordId: payload.verwijderingsRecordId ?? '',
    aantalWasserijItems: payload.aantalWasserijItems ?? 0,
    aantalRitten: payload.aantalRitten ?? 0,
  };
}

export async function fetchAuditLog(orderId: string): Promise<AuditLogEntry[]> {
  const headers = await authHeaders();
  const response = await fetch(`/api/orders/audit-log?orderId=${encodeURIComponent(orderId)}`, { headers });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Audit log mislukt' }));
    throw new Error(err.error || 'Audit log mislukt');
  }
  const payload = await response.json() as { entries?: AuditLogEntry[] };
  return Array.isArray(payload.entries) ? payload.entries : [];
}
